import React from 'react';
import { rem, size } from 'polished';
import styled, { css } from 'styled-components';

import { useLayoutData } from 'hooks/useLayoutData';
import { BasicTooltip, TooltipPlacement } from 'components/GenericTooltip';

import NotificationCounter from '../NotificationCounter/NotificationCounter';

const IconGroupWrapper = styled.div<{ hasMargin: boolean }>`
  position: relative;
  ${({ hasMargin }) => hasMargin && `margin-right: ${rem(12)}`};

  svg {
    ${size('100%')};
    vertical-align: top;
  }
`;

const IconWrapper = styled.div<{ iconScale?: number }>`
  opacity: 0.6;
  transition: opacity 0.2s ease;
  ${size(rem(16))};
  ${({ iconScale }) => iconScale && `transform: scale(${iconScale})`};
`;

export const itemStyles = css`
  display: inline-block;
  color: ${({ theme }) => theme.colors.textLight};
  outline: none;
  transition: color 0.2s ease;
`;

export const itemActiveStyles = css`
  color: ${({ theme }) => theme.colors.textDark};

  ${IconWrapper} {
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CounterAnyValue = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.mainBold};
`;

export interface Props {
  text: React.ReactElement;
  icon?: React.ReactNode | React.ReactNode[];
  iconScale?: number;
  notificationCount?: number;
  showExclamationMark?: boolean;
}

const NavItemContent = ({
  icon,
  iconScale,
  text,
  notificationCount,
  showExclamationMark,
}: Props) => {
  const { isSidebarCollapsed } = useLayoutData();

  const counterValue = showExclamationMark ? (
    <CounterAnyValue>!</CounterAnyValue>
  ) : (
    notificationCount
  );

  const iconComponent = icon && (
    <IconGroupWrapper hasMargin={!isSidebarCollapsed} data-test="item-icon">
      <IconWrapper iconScale={iconScale}>{icon}</IconWrapper>

      {counterValue != null && Boolean(counterValue) && (
        <NotificationCounter topOffset={-2} rightOffset={-2}>
          {counterValue}
        </NotificationCounter>
      )}
    </IconGroupWrapper>
  );

  return (
    <Wrapper>
      {isSidebarCollapsed && iconComponent ? (
        <BasicTooltip tooltipMessage={text} position={TooltipPlacement.Right} hasAccentTheme>
          {iconComponent}
        </BasicTooltip>
      ) : (
        iconComponent
      )}

      {!isSidebarCollapsed && <span>{text}</span>}
    </Wrapper>
  );
};

export default NavItemContent;
